import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';

import TemplatePreview from '../../components/templates/TemplatePreview';
import { useBookingConfigEditor } from './hooks/useBookingConfigEditor';
import { FIELD_LABELS } from './constants';

import {
  GeneralSettingsForm,
  BookingLimitsForm,
  ExcludedDaysForm,
  NotificationTemplatesForm,
  AdvancedSettingsForm,
} from '.';
import { SaveBookingConfigButton } from './components/Buttons/SaveBookingConfigButton';
import { DisableBookingConfirmation } from './components/Dialogs/DisableBookingConfirmation';
import { RangesDeletedConfirmation } from './components/Dialogs/RangesDeletedConfirmation';


interface BookingConfigEditorProps {
  smbId: string;
  onDirtyStateChange?: (isDirty: boolean) => void;
}

export const BookingConfigEditor = ({ smbId, onDirtyStateChange }: BookingConfigEditorProps) => {
  const {
    formData,
    isLoading,
    isSaving,
    isNewConfig,
    error,
    errors,
    validationErrorTarget,
    config,
    selectedTemplates,
    collisions,
    selectedTemplatesData,
    localTemplates,
    templateVerifyErrors,
    activePreviewField,
    setActivePreviewField,
    handleChange,
    handleSave,
    validateForm,
    isDirty,
    getGlobalRangesForSelectedDays,
  } = useBookingConfigEditor(smbId);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rangesEmptyConfirmOpen, setRangesEmptyConfirmOpen] = useState(false);

  const shouldExpandExcludedDays = !!validationErrorTarget?.key.startsWith('excluded_day_');
  const shouldExpandAdvancedSettings = validationErrorTarget?.key === 'first_slot_offset'
    || validationErrorTarget?.key === 'max_booking_window';

  useEffect(() => {
    if (!validationErrorTarget) return;

    const targetKey = validationErrorTarget.key.startsWith('days_info_slot_') && !formData.is_per_day_schedule
      ? 'standard_hours'
      : validationErrorTarget.key;
    const scrollTimer = window.setTimeout(() => {
      const target = Array.from(
        document.querySelectorAll<HTMLElement>('[data-booking-error-key]')
      ).find((el) => el.dataset.bookingErrorKey === targetKey);

      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);

    return () => window.clearTimeout(scrollTimer);
  }, [validationErrorTarget, formData.is_per_day_schedule]);

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyStateChange?.(isDirty);
  }, [isDirty, onDirtyStateChange]);

  // Handle browser close/refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  if (isLoading && !config) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSaveClick = () => {
    if (!validateForm()) return;

    const scheduleSlots = formData.is_per_day_schedule
      ? (formData.days_info?.slots || {})
      : getGlobalRangesForSelectedDays();

    const allRangesEmpty = (formData.days || []).every(day => {
      const ranges = scheduleSlots[String(day)];
      return !ranges || ranges.length === 0;
    });

    if (formData.enabled === false) {
      setConfirmOpen(true);
    } else if (allRangesEmpty) {
      setRangesEmptyConfirmOpen(true);
    } else {
      handleSave();
    }
  };

  const currentTemplate = activePreviewField ? selectedTemplatesData[activePreviewField] || null : null;

  return (
    <Box sx={{ pb: 5 }}>

      {isNewConfig && !error && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>New Setup:</strong> No existing booking configuration found for this SMB.
          Default settings have been loaded for your first-time configuration.
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Left Column: Form Settings */}
        <Grid item xs={12} lg={8}>
          <GeneralSettingsForm
            formData={formData}
            onChange={handleChange}
            errors={errors}
          />

          <BookingLimitsForm
            formData={formData}
            onChange={handleChange}
            errors={errors}
          />

          <ExcludedDaysForm
            formData={formData}
            onChange={handleChange}
            errors={errors}
            timezone={config?.timezone}
            forceExpand={shouldExpandExcludedDays}
          />

          <NotificationTemplatesForm
            smbId={smbId}
            formData={formData}
            onChange={handleChange}
            setActivePreviewField={setActivePreviewField}
            selectedTemplates={selectedTemplates}
            localTemplates={localTemplates}
            templateVerifyErrors={templateVerifyErrors}
            collisions={collisions}
            errors={errors}
          />

          <AdvancedSettingsForm
            formData={formData}
            onChange={handleChange}
            errors={errors}
            forceExpand={shouldExpandAdvancedSettings}
          />

          <SaveBookingConfigButton
            isSaving={isSaving}
            disabled={!isDirty || Object.values(collisions).some(c => c === true)}
            onSave={handleSaveClick}
          />
        </Grid>

        {/* Right Column: Sticky Live Preview */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ position: 'sticky', top: 75 }}>
            <TemplatePreview
              template={currentTemplate}
              title="Template Preview"
              subtitle={activePreviewField && currentTemplate && (
                <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  Showing preview for: <strong>{FIELD_LABELS[activePreviewField] || activePreviewField.replace(/_/g, ' ')}</strong>
                </Typography>
              )}
            />
          </Box>
        </Grid>
      </Grid>

      <DisableBookingConfirmation
        confirmOpen={confirmOpen}
        setConfirmOpen={setConfirmOpen}
        handleSave={handleSave}
      />

      <RangesDeletedConfirmation
        confirmOpen={rangesEmptyConfirmOpen}
        setConfirmOpen={setRangesEmptyConfirmOpen}
        handleSave={handleSave}
      />

    </Box>
  );
};
